/**
 * SPEC v0.9.5 §3 — the "สรุป" level for one point. ONE deterministic function (no LLM,
 * no randomness): the highest level whose condition is met by REAL inputs, with the
 * reasons built only from the conditions that actually fired.
 *
 *   flooded   🔴 มีน้ำท่วมแล้ว            GISTDA flood in radius · road gauge ≥20 cm · ThaiWater ≥0.05 m over bank
 *   prepare   🟠 ควรเตรียมตัว             Part D decideAdvice() shows
 *   watch     🟡 ควรติดตาม                ปภ./สทนช. notice in force · upstream release amber · upstream ≥100% ·
 *                                          storm ≤500 km · ThaiWater station level with / near the bank
 *   clear     🟢 ยังไม่พบสัญญาณน้ำท่วม    none of the above AND core data present
 *                                          (GISTDA + rain + at least one of upstream dam / station)
 *   insufficient ⚪ ข้อมูลไม่พอสรุป        core data missing → says what is missing (never falls to 🟢)
 *   pending   (กำลังโหลด…)                core data still loading and nothing higher fired yet
 *
 * A higher level may be reported while lower dimensions are still loading
 * (`provisional` says whether a still-loading input could raise it further).
 */
export const SUMMARY_LABEL = {
    flooded: "มีน้ำท่วมแล้ว",
    prepare: "ควรเตรียมตัว",
    watch: "ควรติดตาม",
    clear: "ยังไม่พบสัญญาณน้ำท่วม",
    insufficient: "ข้อมูลไม่พอสรุป",
    pending: "กำลังโหลด…",
};
export const SUMMARY_TODO = {
    flooded: "ดูระดับน้ำใกล้คุณด้านล่าง ยกของและปลั๊กไฟขึ้นที่สูง ติดตามประกาศ ปภ.",
    prepare: "ควรเตรียมยกของขึ้นที่สูง และติดตามประกาศ ปภ./กรมอุตุฯ",
    watch: "ติดตามประกาศ ปภ. และกลับมาเช็กอีกครั้งวันนี้",
    clear: "ยังไม่ต้องทำอะไรเพิ่ม กลับมาเช็กเมื่อฝนตกหนักหรือมีประกาศ",
    insufficient: null,
    pending: null,
};
/** Thresholds (SPEC v0.9.5 §3). */
export const SUMMARY_RULES = { roadFloodCm: 20, overBankM: 0.05, damFullPct: 100, riverMaxKm: 30, ddpmStaleHours: 72 };
/** D1: is the nearest main river close enough for the summary to follow it (dam / C.13 / tide rows)? */
export function riverInReach(snapDistKm) {
    return snapDistKm != null && Number.isFinite(snapDistKm) && snapDistKm <= SUMMARY_RULES.riverMaxKm;
}
const ok = (d) => d.state === "ok";
const fmt2 = (v) => v.toFixed(2);
export function decideSummary(i) {
    const out = (level, reasons, missing = [], provisional = false) => ({ level, label: SUMMARY_LABEL[level], reasons, todo: SUMMARY_TODO[level], missing, provisional });
    const loading = (...ds) => ds.some((d) => d.state === "loading");
    // 🔴 already flooded — real observations only
    const red = [];
    if (ok(i.gistda) && i.gistda.value.floodedInRadius)
        red.push("ดาวเทียม GISTDA เห็นน้ำท่วมในรัศมี");
    if (ok(i.roadGauge) && i.roadGauge.value.maxDepthCm != null && i.roadGauge.value.maxDepthCm >= SUMMARY_RULES.roadFloodCm) {
        red.push(`จุดวัดถนน${i.roadGauge.value.name ? " " + i.roadGauge.value.name : ""} น้ำลึก ${Math.round(i.roadGauge.value.maxDepthCm)} ซม.`);
    }
    if (ok(i.station) && i.station.value.maxOverBankM != null && i.station.value.maxOverBankM >= SUMMARY_RULES.overBankM) {
        red.push(`สถานี${i.station.value.overName ? " " + i.station.value.overName : ""} น้ำสูงกว่าตลิ่ง ${fmt2(i.station.value.maxOverBankM)} ม.`);
    }
    if (red.length)
        return out("flooded", red);
    const couldBeRed = loading(i.gistda, i.roadGauge, i.station);
    // 🟠 prepare — Part D
    if (ok(i.advice) && i.advice.value.show)
        return out("prepare", [i.advice.value.reason], [], couldBeRed);
    const couldBeOrange = couldBeRed || loading(i.advice, i.rain);
    // 🟡 watch
    const yellow = [];
    const ddpmOn = ok(i.ddpm) && i.ddpm.value.declared;
    if (ddpmOn) {
        const v = i.ddpm.value;
        yellow.push(`อำเภอนี้มีประกาศเขตภัยพิบัติ (ปภ.${v.dateText ? " ณ " + v.dateText : ""})`);
    }
    if (ok(i.onwr) && i.onwr.value)
        yellow.push("มีประกาศเฝ้าระวังน้ำทะเลหนุนสูง (สทนช.) ของจังหวัดนี้");
    if (ok(i.upstreamDam)) {
        const d = i.upstreamDam.value;
        if (d.releaseAmber)
            yellow.push(d.releaseText ?? `${d.name}ระบายเพิ่มจากรายงานก่อน`);
        if (d.percent != null && d.percent >= SUMMARY_RULES.damFullPct)
            yellow.push(`${d.name}เกินความจุ ${Math.round(d.percent)}%`);
    }
    if (ok(i.storm) && i.storm.value.count > 0)
        yellow.push(`มีพายุ${i.storm.value.name ? " " + i.storm.value.name : ""}ในระยะ 500 กม. จากไทย (GDACS)`);
    if (ok(i.station) && i.station.value.nearBank)
        yellow.push(`สถานี${i.station.value.nearName ? " " + i.station.value.nearName : ""} ระดับน้ำใกล้ตลิ่ง`);
    const outside = ok(i.station) ? i.station.value.outside : null;
    if (outside && outside.overBankM != null && outside.overBankM >= SUMMARY_RULES.overBankM) {
        yellow.push(`สถานี ${outside.name} (ห่าง ${outside.distKm.toFixed(1)} กม. นอกรัศมี) น้ำสูงกว่าตลิ่ง ${fmt2(outside.overBankM)} ม.`);
    }
    if (ok(i.rain) && i.rain.value.splitHeavyDays.length)
        yellow.push(`บางแบบจำลองคาดฝนหนักวัน${i.rain.value.splitHeavyDays.join(", ")}`);
    if (yellow.length) {
        // C1: when the ONLY reason is a ปภ. announcement older than 72 h, say that it was not re-checked since
        if (yellow.length === 1 && ddpmOn && i.ddpm.value.stale) {
            yellow[0] += " · ยังไม่ได้ตรวจการยกเลิกประกาศหลังวันนั้น";
        }
        return out("watch", yellow, [], couldBeOrange);
    }
    // core data: GISTDA + rain + (upstream dam or station)
    const missing = [];
    if (!ok(i.gistda))
        missing.push("ดาวเทียม GISTDA");
    if (!ok(i.rain))
        missing.push("ฝนพยากรณ์ที่จุดนี้");
    const hasDam = ok(i.upstreamDam), hasStation = ok(i.station) && (i.station.value.count > 0 || !!i.station.value.outside);
    if (!hasDam && !hasStation)
        missing.push("เขื่อนต้นทาง/สถานีวัดระดับน้ำในรัศมี");
    // anything still loading (core or a dimension that could still raise the level) → not green yet
    const anyLoading = loading(i.gistda, i.rain, i.upstreamDam, i.station, i.roadGauge, i.advice, i.ddpm, i.onwr, i.storm);
    if (anyLoading)
        return out("pending", [], [], true);
    if (missing.length)
        return out("insufficient", [], missing);
    return out("clear", []);
}
//# sourceMappingURL=summary.js.map